import { useState, useRef, useEffect, useCallback } from "react";
import { MatchEvent } from "../../../store/useMatchLogStore";
import { updateMatchEvent } from "../../../lib/loggerApi";
import type { GoalPlacement } from "../utils/goalFrame";

export type RefinementChipEventType = "Shot" | "Duel";

export interface ChipState {
  eventId: string;
  eventType: RefinementChipEventType;
  /** PMA-COCK-182: the confirmed event itself, so `RefinementChipPanel` can
   * decide (`isPlacementEligible`) whether to show the goal-frame capture
   * above the body-part chips. */
  event: MatchEvent;
}

interface UseRefinementChipsParams {
  recentEvents: MatchEvent[];
}

interface UseRefinementChipsReturn {
  chipState: ChipState | null;
  handleChipTap: (chip: string) => void;
  dismissChipPanel: () => void;
  triggerChipForAction: (eventType: RefinementChipEventType) => void;
  /** PMA-COCK-182: a tap on the live goal-frame. Best-effort (same silent
   * error policy as `handleChipTap`) and — unlike a chip tap — does NOT
   * dismiss the panel; the chips row and Skip keep working afterward, and a
   * body-part tap and a placement tap can land on the same event in either
   * order. PMA-COCK-185: returns the PUT's settlement (always resolves,
   * even on failure — the catch below swallows the error) so
   * `GoalPlacementOverlay` can close itself only once the PUT has settled,
   * with its own minimum-visible-time floor on top. */
  handlePlacementTap: (placement: GoalPlacement) => Promise<void>;
  /** The placement already sent for the current `chipState.eventId`, so the
   * frame can render itself as placed. Reset whenever `chipState` changes or
   * the panel is dismissed. */
  placedPlacement: GoalPlacement | null;
}

const countConfirmed = (
  events: MatchEvent[],
  type: RefinementChipEventType,
): number =>
  events.filter((e) => e.type === type && e._confirmed && e._id).length;

export const useRefinementChips = ({
  recentEvents,
}: UseRefinementChipsParams): UseRefinementChipsReturn => {
  const [chipState, setChipState] = useState<ChipState | null>(null);
  const [placedPlacement, setPlacedPlacement] = useState<GoalPlacement | null>(
    null,
  );

  // Holds pending chip watch: type + baseline confirmed-event count at dispatch time.
  const pendingRef = useRef<{
    type: RefinementChipEventType;
    baseCount: number;
  } | null>(null);

  // Keep a stable reference to recentEvents so triggerChipForAction is stable.
  const recentEventsRef = useRef(recentEvents);
  useEffect(() => {
    recentEventsRef.current = recentEvents;
  });

  // Called immediately after dispatchEvent sends a Shot or Duel.
  const triggerChipForAction = useCallback(
    (eventType: RefinementChipEventType) => {
      const baseCount = countConfirmed(recentEventsRef.current, eventType);
      pendingRef.current = { type: eventType, baseCount };
    },
    [],
  );

  // Watch recentEvents for the first new confirmed event matching the pending type.
  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    const confirmedEvents = recentEvents.filter(
      (e) => e.type === pending.type && e._confirmed && e._id,
    );
    if (confirmedEvents.length > pending.baseCount) {
      const newest = confirmedEvents[confirmedEvents.length - 1];
      pendingRef.current = null;
      setPlacedPlacement(null);
      setChipState({
        eventId: newest._id!,
        eventType: pending.type,
        event: newest,
      });
    }
  }, [recentEvents]);

  const handleChipTap = useCallback(
    (chip: string) => {
      if (!chipState) return;
      const { eventId, eventType } = chipState;
      setChipState(null);
      setPlacedPlacement(null);
      // PMA-COCK-183: body-part chips are retired for Shot — SHOT_CHIPS is
      // empty so no UI can reach this branch with eventType "Shot" anymore,
      // but the hook itself must never issue a body_part PUT either. Duel
      // (Ground/Aerial) is unaffected.
      if (eventType === "Duel") {
        updateMatchEvent(eventId, { data: { duel_type: chip } }).catch(() => {
          // best-effort: silently discard errors per PMA-COCK-049 AC5
        });
      }
    },
    [chipState],
  );

  const dismissChipPanel = useCallback(() => {
    pendingRef.current = null;
    setChipState(null);
    setPlacedPlacement(null);
  }, []);

  // PMA-COCK-182: a tap on the live goal-frame sends ONLY `goal_placement` —
  // never spread `event.data` forward here, the backend's key-wise `data`
  // merge (PMA-EVENT-018) is what keeps `outcome`/`shot_type`/etc. intact.
  // Unlike `handleChipTap`, this does not clear `chipState` — the panel (and
  // the chips row / Skip) stays usable so a body-part tap can still follow.
  const handlePlacementTap = useCallback(
    (placement: GoalPlacement): Promise<void> => {
      if (!chipState) return Promise.resolve();
      const { eventId } = chipState;
      setPlacedPlacement(placement);
      return updateMatchEvent(eventId, {
        data: { goal_placement: { ...placement, source: "live" } },
      })
        .then(() => undefined)
        .catch(() => {
          // best-effort: silently discard errors per PMA-COCK-049 AC5
        });
    },
    [chipState],
  );

  return {
    chipState,
    handleChipTap,
    dismissChipPanel,
    triggerChipForAction,
    handlePlacementTap,
    placedPlacement,
  };
};
